import { logger } from '@/lib/logger';
import type { ExperienceFont, ResolvedVisual } from '@matter_extractor/shared';
import { fontsInUse } from '@matter_extractor/shared';

/**
 * The five self-hosted families of §9.4 (H24), loaded ONLY when a theme names
 * one.
 *
 * « Auto-hébergées et jamais servies par un CDN : une expérience se joue chez
 * des gens dont le navigateur n'a rien à demander à un tiers. » The four
 * families that are not the admin's own arrive as `@fontsource/*` packages, and
 * a dynamic `import()` of one weight's stylesheet is what makes it a chunk Vite
 * emits next to the bundle - self-hosted, with no file copied by hand and no
 * `@font-face` written twice.
 *
 * « Cinq familles en trois graisses font quinze fichiers, et les servir tous à
 * chaque session paierait quatorze fois rien »: hence the load on demand, and
 * hence `fontsInUse`, which answers the two faces a resolved theme names.
 *
 * ## The `latin` cut, and only it
 *
 * Each package ships its faces cut by unicode range. The `latin` cut covers
 * French - accents included - in ONE file per weight, where the full stylesheet
 * would have Vite emit the cyrillic, greek and vietnamese cuts of every weight
 * as well. A glyph outside the cut falls back per character, which is the
 * browser's normal behaviour and not a defect.
 *
 * ## Titillium Web is absent from this table on purpose
 *
 * It is served globally by `styles.css` from `public/fonts`, in the five
 * weights the admin uses. Loading it again here would fetch the same faces
 * under a second set of `@font-face` rules.
 *
 * ## Caveat has no italic
 *
 * Google ships Caveat in upright weights only. Its entry therefore carries two
 * loaders rather than three, and a passage marked italic in a Caveat theme is
 * slanted by the browser. A third entry resolving to nothing would have been a
 * promise the package cannot keep.
 *
 * ## Nothing is memoised here
 *
 * A dynamic `import()` of a module already loaded resolves from the module
 * registry without a second request, so a cache of « families already asked
 * for » would only skip re-running this list.
 */

type FontLoader = () => Promise<unknown>;

const LOADERS: Readonly<Record<ExperienceFont, readonly FontLoader[]>> = {
  /* Served by `styles.css` - see the note above. */
  'titillium-web': [],
  'source-sans-3': [
    () => import('@fontsource/source-sans-3/latin-400.css'),
    () => import('@fontsource/source-sans-3/latin-700.css'),
    () => import('@fontsource/source-sans-3/latin-400-italic.css'),
  ],
  lora: [
    () => import('@fontsource/lora/latin-400.css'),
    () => import('@fontsource/lora/latin-700.css'),
    () => import('@fontsource/lora/latin-400-italic.css'),
  ],
  'playfair-display': [
    () => import('@fontsource/playfair-display/latin-400.css'),
    () => import('@fontsource/playfair-display/latin-700.css'),
    () => import('@fontsource/playfair-display/latin-400-italic.css'),
  ],
  caveat: [
    () => import('@fontsource/caveat/latin-400.css'),
    () => import('@fontsource/caveat/latin-700.css'),
  ],
};

/** One of the five families of §9.4, and nothing else. */
export function isExperienceFont(name: string): name is ExperienceFont {
  return Object.hasOwn(LOADERS, name);
}

/**
 * The families a resolved theme names that are not already served.
 *
 * PURE, so the rule « n'est chargée que si un thème la nomme » is testable
 * without a bundler: `fontsInUse` answers the two faces, this drops the ones
 * with nothing to load.
 */
export function fontsToLoad(visual: ResolvedVisual): ExperienceFont[] {
  return fontsInUse(visual)
    .filter(isExperienceFont)
    .filter((font) => LOADERS[font].length > 0);
}

/**
 * Loads the faces a theme names.
 *
 * A face that fails to arrive is a DEGRADATION and not an error state: the page
 * stays readable in its fallback stack, and the person must never be shown a
 * broken screen over a typeface. So the failure is warned about - the operator
 * can find it - and never rethrown.
 */
export async function loadExperienceFonts(visual: ResolvedVisual): Promise<void> {
  await Promise.all(
    fontsToLoad(visual).flatMap((font) =>
      LOADERS[font].map(async (load) => {
        try {
          await load();
        } catch (cause) {
          logger.warn(`reader: font ${font} could not be loaded`, cause);
        }
      }),
    ),
  );
}
